import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, List, TrendingUp, Loader2 } from "lucide-react";
import { useStrengths } from "@/hooks/use-strengths";
import { getStrengthDescription, translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";

interface UserStrength {
  rank: number;
  name: string;
  domain: string;
  score?: number;
  description?: string;
  shortDescription?: string;
  nameAr?: string;
}

interface UserStrengthsViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  userEmail?: string;
  strengths: UserStrength[];
  embedded?: boolean;
}

const getStrengthDisplayName = (
  strength: { name: string; nameAr?: string | null },
  t: ReturnType<typeof useTranslation>["t"],
  language: string
) => {
  const englishName = strength.name;
  const arabicName = strength.nameAr || null;

  if (language?.split("-")[0] === "ar") {
    return {
      primary: arabicName || englishName,
      secondary: arabicName ? englishName : null,
    };
  }

  return {
    primary: englishName,
    secondary: arabicName ? arabicName : null,
  };
};

const ALL_CLIFTON_STRENGTHS = [
  // Executing Domain
  { name: "Achiever", domain: "Executing" },
  { name: "Arranger", domain: "Executing" },
  { name: "Belief", domain: "Executing" },
  { name: "Consistency", domain: "Executing" },
  { name: "Deliberative", domain: "Executing" },
  { name: "Discipline", domain: "Executing" },
  { name: "Focus", domain: "Executing" },
  { name: "Responsibility", domain: "Executing" },
  { name: "Restorative", domain: "Executing" },
  // Influencing Domain
  { name: "Activator", domain: "Influencing" },
  { name: "Command", domain: "Influencing" },
  { name: "Communication", domain: "Influencing" },
  { name: "Competition", domain: "Influencing" },
  { name: "Maximizer", domain: "Influencing" },
  { name: "Self-Assurance", domain: "Influencing" },
  { name: "Significance", domain: "Influencing" },
  { name: "Woo", domain: "Influencing" },
  // Relationship Building Domain
  { name: "Adaptability", domain: "Relationship Building" },
  { name: "Connectedness", domain: "Relationship Building" },
  { name: "Developer", domain: "Relationship Building" },
  { name: "Empathy", domain: "Relationship Building" },
  { name: "Harmony", domain: "Relationship Building" },
  { name: "Includer", domain: "Relationship Building" },
  { name: "Individualization", domain: "Relationship Building" },
  { name: "Positivity", domain: "Relationship Building" },
  { name: "Relator", domain: "Relationship Building" },
  // Strategic Thinking Domain
  { name: "Analytical", domain: "Strategic Thinking" },
  { name: "Context", domain: "Strategic Thinking" },
  { name: "Futuristic", domain: "Strategic Thinking" },
  { name: "Ideation", domain: "Strategic Thinking" },
  { name: "Input", domain: "Strategic Thinking" },
  { name: "Intellection", domain: "Strategic Thinking" },
  { name: "Learner", domain: "Strategic Thinking" },
  { name: "Strategic", domain: "Strategic Thinking" },
];

export default function UserStrengthsView({
  open,
  onOpenChange,
  userName,
  userEmail,
  strengths,
  embedded = false,
}: UserStrengthsViewProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.split("-")[0] === "ar";
  const { getUserRanks, isLoading } = useStrengths();
  const [currentTab, setCurrentTab] = useState("all");
  const [topStrengthsData, setTopStrengthsData] = useState<UserStrength[]>([]);
  const [domainStrengthsData, setDomainStrengthsData] = useState<Record<string, UserStrength[]>>({});
  const [isLoadingTop, setIsLoadingTop] = useState(false);
  const [isLoadingDomain, setIsLoadingDomain] = useState(false);

  // Fetch top 10 strengths when top tab is selected
  useEffect(() => {
    if (currentTab === "top" && open && userEmail && topStrengthsData.length === 0) {
      const fetchTopStrengths = async () => {
        setIsLoadingTop(true);
        const result = await getUserRanks(userEmail, "top10");
        if (result.success && result.data?.strengths) {
          const transformed = result.data.strengths.map((s: any) => ({
            rank: s.rank,
            name: s.strengthId.name,
            nameAr: s.strengthId.nameAr,
            domain: s.strengthId.domain,
            score: s.score,
            description: s.strengthId.description,
            shortDescription: s.strengthId.shortDescription,
          }));
          setTopStrengthsData(transformed);
        }
        setIsLoadingTop(false);
      };
      fetchTopStrengths();
    }
  }, [currentTab, open, userEmail]);

  // Fetch strengths by domain when domain tab is selected
  useEffect(() => {
    if (currentTab === "domains" && open && userEmail && Object.keys(domainStrengthsData).length === 0) {
      const fetchDomainStrengths = async () => {
        setIsLoadingDomain(true);
        const result = await getUserRanks(userEmail, "bydomain");
        if (result.success && result.data?.strengthsByDomain) {
          const transformed: Record<string, UserStrength[]> = {};
          Object.entries(result.data.strengthsByDomain).forEach(([domain, items]: [string, any]) => {
            transformed[domain] = items.map((s: any) => ({
              rank: s.rank,
              name: s.strengthId.name,
              nameAr: s.strengthId.nameAr,
              domain: s.strengthId.domain,
              score: s.score,
              description: s.strengthId.description,
              shortDescription: s.strengthId.shortDescription,
            }));
          });
          setDomainStrengthsData(transformed);
        }
        setIsLoadingDomain(false);
      };
      fetchDomainStrengths();
    }
  }, [currentTab, open, userEmail]);

  // Reset data when dialog closes
  useEffect(() => {
    if (!open) {
      setTopStrengthsData([]);
      setDomainStrengthsData({});
      setCurrentTab("all");
    }
  }, [open]);

  const getDomainColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return "bg-primary/10 text-primary border-primary/25";
      case "Executing":
        return "bg-secondary/10 text-brand-green-dark border-secondary/25";
      case "Influencing":
        return "bg-warning/10 text-warning border-warning/25";
      case "Relationship Building":
        return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
      default:
        return "bg-secondary/20 text-secondary border-secondary/30";
    }
  };

  const getDomainBgColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return "bg-primary/5";
      case "Executing":
        return "bg-secondary/5";
      case "Influencing":
        return "bg-warning/5";
      case "Relationship Building":
        return "bg-brand-teal-dark/5";
      default:
        return "bg-secondary/10";
    }
  };

  const getRankColor = (rank: number) => {
    if (rank <= 5) return "text-primary font-bold";
    if (rank <= 10) return "text-brand-green-dark font-semibold";
    if (rank <= 15) return "text-warning";
    return "text-muted-foreground";
  };

  const getRankBadge = (rank: number) => {
    if (rank <= 5) return "bg-primary/10 text-primary border-primary/25";
    if (rank <= 10) return "bg-secondary/10 text-brand-green-dark border-secondary/25";
    if (rank <= 15) return "bg-warning/10 text-warning border-warning/25";
    return "bg-secondary/20 text-muted-foreground border-secondary/30";
  };

  // Calculate domain distribution
  const domainCounts = strengths.reduce((acc, strength) => {
    acc[strength.domain] = (acc[strength.domain] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topDomains = Object.entries(domainCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2);

  // Group strengths by domain (for "all" tab)
  const strengthsByDomain = strengths.reduce((acc, strength) => {
    const domain = strength.domain || "Other";
    if (!acc[domain]) {
      acc[domain] = [];
    }
    acc[domain].push(strength);
    return acc;
  }, {} as Record<string, UserStrength[]>);

  // Sort each domain group by rank
  Object.keys(strengthsByDomain).forEach((domain) => {
    strengthsByDomain[domain].sort((a, b) => a.rank - b.rank);
  });

  // Use data from API for top tab, fallback to filtered strengths
  const displayTopStrengths = topStrengthsData.length > 0 
    ? topStrengthsData 
    : strengths.filter((s) => s.rank <= 10).sort((a, b) => a.rank - b.rank);

  // Use data from API for domain tab, fallback to grouped strengths
  const displayDomainStrengths = Object.keys(domainStrengthsData).length > 0
    ? domainStrengthsData
    : strengthsByDomain;

  const profileSummary = (
    <div className="space-y-1 mt-2 min-w-0">
      <p className="break-words text-base font-semibold text-foreground">{userName}</p>
      {userEmail && <p className="break-all text-sm">{userEmail}</p>}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Badge variant="outline" className="text-xs">
          {strengths.length} / 34 {t('admin.strengthsAssessed')}
        </Badge>
        {topDomains.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-muted-foreground">{t('admin.topDomains')}:</span>
            {topDomains.map(([domain, count]) => (
              <Badge key={domain} className={`${getDomainColor(domain)} text-xs`}>
                {isArabic ? translateDomainName(domain, t, i18n.language) : domain.split(' ')[0]} ({count})
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const localizedStrengthDescription = (strength: UserStrength) =>
    getStrengthDescription(
      strength.name,
      strength.domain,
      strength.shortDescription || strength.description,
      t,
      i18n.language
    );

  const content = (
    <>
        {embedded ? (
          <div className="flex flex-col space-y-1.5 text-center sm:text-left">
            <h2 className="flex flex-wrap items-center gap-3 text-lg font-semibold leading-tight">
              <TrendingUp className="w-6 h-6 text-primary" />
              {t('admin.userStrengthsProfile')}
            </h2>
            <div className="text-sm text-muted-foreground">
              {profileSummary}
            </div>
          </div>
        ) : (
          <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            <TrendingUp className="w-6 h-6 text-primary" />
            {t('admin.userStrengthsProfile')}
          </DialogTitle>
          <DialogDescription>
            {profileSummary}
          </DialogDescription>
        </DialogHeader>
        )}

        <Tabs
          defaultValue="all"
          className="mt-4 flex-1 flex min-w-0 flex-col overflow-hidden sm:mt-5"
          value={currentTab}
          onValueChange={setCurrentTab}
          dir={isArabic ? "rtl" : "ltr"}
        >
          <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
          <TabsList className="grid w-max min-w-full grid-cols-3">
            <TabsTrigger value="all" className="shrink-0 px-3">
              <List className="w-4 h-4 me-2" />
              {t('admin.allStrengths')}
            </TabsTrigger>
            <TabsTrigger value="top" className="shrink-0 px-3">
              <TrendingUp className="w-4 h-4 me-2" />
              {isArabic ? t("publicProfile.topStrengths") : "Top 10"}
            </TabsTrigger>
            <TabsTrigger value="domains" className="shrink-0 px-3">
              <BarChart3 className="w-4 h-4 me-2" />
              {t('admin.byDomain')}
            </TabsTrigger>
          </TabsList>
          </div>

          {/* All Strengths Tab */}
          <TabsContent value="all" className="flex-1 mt-4 overflow-hidden">
            <ScrollArea className="h-[60vh] max-h-[550px] pr-0 sm:pr-4">
              <div className="space-y-2 pb-8">
                {strengths.length > 0 ? (
                  [...strengths]
                    .sort((a, b) => a.rank - b.rank)
                    .map((strength) => {
                      const domainColor = STRENGTHS_EXPLORER_DOMAIN_COLORS[strength.domain];
                      return (
                      <div
                        key={strength.rank}
                         dir={isArabic ? "rtl" : "ltr"}
                         className="flex min-w-0 flex-col gap-3 rounded-lg border border-s-4 p-3 text-start sm:flex-row sm:items-center sm:justify-between sm:p-4 hover:shadow-md transition-all"
                         style={domainColor ? { borderInlineStartColor: domainColor, backgroundColor: `${domainColor}0d` } : undefined}
                       >
                         <div className={`flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4 ${
                           isArabic ? "text-right" : ""
                         }`}>
                           <Badge variant="outline" className={`${getRankBadge(strength.rank)} min-w-[52px] shrink-0 justify-center sm:min-w-[60px]`}>
                             #{strength.rank}
                           </Badge>
                           <div className="min-w-0 flex-1">
                             <div className="flex min-w-0 flex-col gap-1">
                               <p className={`break-words font-semibold ${getRankColor(strength.rank)}`}>
                                {getStrengthDisplayName(strength, t, i18n.language).primary}
                              </p>
                              {getStrengthDisplayName(strength, t, i18n.language).secondary && (
                                <span className="text-[10px] font-medium opacity-75 tracking-wide" dir="ltr">
                                  {getStrengthDisplayName(strength, t, i18n.language).secondary}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 break-words text-sm text-muted-foreground">
                              {localizedStrengthDescription(strength)}
                            </p>
                            <Badge
                              className="mt-1 border"
                              variant="outline"
                              style={domainColor ? { backgroundColor: `${domainColor}15`, borderColor: `${domainColor}40`, color: domainColor } : undefined}
                            >
                              {translateDomainName(strength.domain, t, i18n.language)}
                            </Badge>
                          </div>
                        </div>
                        {strength.score && (
                           <div className="text-start sm:text-right">
                            <p className="text-sm text-muted-foreground">{t('admin.score')}</p>
                            <p className="text-lg font-bold text-primary">{strength.score}</p>
                          </div>
                        )}
                      </div>
                      );
                    })
                ) : (
                  <Card className="p-8 text-center">
                    <p className="text-muted-foreground">{t('admin.noStrengthsData')}</p>
                  </Card>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Top 10 Strengths Tab */}
          <TabsContent value="top" className="flex-1 mt-4 overflow-hidden">
            <ScrollArea className="h-[60vh] max-h-[550px] pr-0 sm:pr-4">
              {isLoadingTop ? (
                <div className="flex items-center justify-center h-[400px]">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4 pb-8">
                  {displayTopStrengths.length > 0 ? (
                    displayTopStrengths.map((strength, index) => (
                      <Card
                        key={strength.rank}
                         dir={isArabic ? "rtl" : "ltr"}
                         className={`p-4 text-start sm:p-6 ${getDomainBgColor(strength.domain)} border-2 hover:shadow-lg transition-all`}
                       >
                         <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start">
                           <div className={`${isArabic ? "text-right sm:text-center" : "text-start sm:text-center"} sm:min-w-[80px]`}>
                             <div className="text-3xl font-bold text-primary mb-2 sm:text-5xl">
                               #{strength.rank}
                             </div>
                            <Badge className={getDomainColor(strength.domain)} variant="outline">
                              {t('admin.top')} {index + 1}
                            </Badge>
                          </div>
                           <div className={`min-w-0 flex-1 ${isArabic ? "text-right" : ""}`}>
                             <div className="mb-2 flex flex-col gap-1">
                               <h3 className="break-words text-xl font-bold sm:text-2xl">
                                {getStrengthDisplayName(strength, t, i18n.language).primary}
                              </h3>
                              {getStrengthDisplayName(strength, t, i18n.language).secondary && (
                                <span className="text-[10px] font-medium opacity-75 tracking-wide" dir="ltr">
                                  {getStrengthDisplayName(strength, t, i18n.language).secondary}
                                </span>
                              )}
                            </div>
                            <Badge className={getDomainColor(strength.domain)} variant="outline">
                              {translateDomainName(strength.domain, t, i18n.language)}
                            </Badge>
                            {strength.score && (
                              <div className="mt-4">
                                <p className="text-sm text-muted-foreground mb-1">{t('admin.strengthScore')}</p>
                                <div className="flex min-w-0 items-center gap-2">
                                  <div className="flex-1 bg-secondary/30 rounded-full h-3">
                                    <div
                                      className="bg-primary h-3 rounded-full transition-all"
                                      style={{ width: `${strength.score}%` }}
                                    ></div>
                                  </div>
                                  <span className="font-bold text-primary">{strength.score}%</span>
                                </div>
                              </div>
                            )}
                             <p className="break-words text-sm text-muted-foreground mt-3">
                              {localizedStrengthDescription(strength)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <Card className="p-8 text-center">
                      <p className="text-muted-foreground">{t('admin.noTopStrengthsData')}</p>
                    </Card>
                  )}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* By Domain Tab */}
          <TabsContent value="domains" className="flex-1 mt-4 overflow-hidden">
            <ScrollArea className="h-[60vh] max-h-[550px] pr-0 sm:pr-4">
              {isLoadingDomain ? (
                <div className="flex items-center justify-center h-[400px]">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-6 pb-8">
                  {Object.entries(displayDomainStrengths).length > 0 ? (
                    ["Strategic Thinking", "Executing", "Influencing", "Relationship Building", "Other"].map((domain) => {
                      const domainStrengths = displayDomainStrengths[domain] || [];
                      if (domainStrengths.length === 0) return null;

                      return (
                        <Card key={domain} className={`p-4 sm:p-6 ${getDomainBgColor(domain)}`}>
                          <div
                            dir={isArabic ? "rtl" : "ltr"}
                            className={`flex flex-col gap-3 mb-4 text-start sm:flex-row sm:items-center sm:justify-between ${
                              isArabic ? "text-right" : ""
                            }`}
                          >
                            <h3 className="text-lg font-bold flex flex-wrap items-center gap-2 sm:text-xl">
                              <Badge className={getDomainColor(domain)} variant="outline">
                                {translateDomainName(domain, t, i18n.language)}
                              </Badge>
                            </h3>
                            <Badge variant="outline">
                              {domainStrengths.length} {t('admin.strengths')}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {domainStrengths.map((strength) => (
                              <div
                                key={strength.rank}
                                dir={isArabic ? "rtl" : "ltr"}
                                className={`flex min-w-0 flex-col gap-2 p-3 bg-background/50 rounded-lg text-start sm:flex-row sm:items-center sm:justify-between ${
                                  isArabic ? "text-right" : ""
                                }`}
                              >
                                <div className="flex min-w-0 items-start gap-3">
                                  <Badge variant="outline" className={getRankBadge(strength.rank)}>
                                    #{strength.rank}
                                  </Badge>
                                  <div className="min-w-0">
                                    <div className="flex min-w-0 flex-col gap-1">
                                      <p className={`min-w-0 break-words font-medium ${getRankColor(strength.rank)}`}>
                                        {getStrengthDisplayName(strength, t, i18n.language).primary}
                                      </p>
                                      {getStrengthDisplayName(strength, t, i18n.language).secondary && (
                                        <span className="text-[10px] font-medium opacity-75 tracking-wide" dir="ltr">
                                          {getStrengthDisplayName(strength, t, i18n.language).secondary}
                                        </span>
                                      )}
                                    </div>
                                    <p className="mt-1 break-words text-sm text-muted-foreground">
                                      {localizedStrengthDescription(strength)}
                                    </p>
                                  </div>
                                </div>
                                {strength.score && (
                                  <span className="text-sm font-semibold text-primary">
                                    {strength.score}%
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </Card>
                      );
                    })
                  ) : (
                    <Card className="p-8 text-center">
                      <p className="text-muted-foreground">{t('admin.noDomainData')}</p>
                    </Card>
                  )}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
    </>
  );

  if (embedded) {
    return (
      <div
        className="flex min-w-0 flex-col overflow-hidden rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6"
        dir={isArabic ? "rtl" : "ltr"}
      >
        {content}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6"
        dir={isArabic ? "rtl" : "ltr"}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
